// Filters
import moment from 'moment-timezone';
import { JobStatus } from "../crawler/crawler.enums";
import { ActiveFilter, Pagination, ParamType } from "../crawler/crawler.interface";
import { __isDateRangeValid } from "./dateTimeUtils";
import { __isStringTrueOrFalse } from "./utils";

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// turns array of ActiveFilter[] objects into API query string
export const __assignFilterStringJobsView = (activeFilters: ActiveFilter[], pagination?: Pagination): string => {
    let filterString = '';
    activeFilters.forEach((filter: ActiveFilter) => {
        switch (filter.name) {
            case 'created_at':
                filterString += `/created_at/range:${localDateToUTC(filter.value)}`;
                break;
            case 'name':
                filterString += `/name/contains:${filter.value}`;
                break;
            case 'owner':
                filterString += `/owner/${filter.value}`;
                break;
            case 'reviewed':
                filter.value === 'clear-filter' ? '' : filterString += `/reviewed/${filter.value}`;
                break;
            case 'status':
                if (filter.value !== '') {
                    filterString += `/status/${filter.value}`;
                }
                break;
            case 'requests':
                filterString += `/requests/contains:${filter.value}`;
                break;
            default:
                return '';
        }
    });
    // Sorting and Pagination must come after the filters
    const sortIndex = activeFilters.findIndex((activeFilter: ActiveFilter) => activeFilter.name.includes('sort'));
    if (sortIndex > -1) {
        filterString += `&sort=${activeFilters[sortIndex].value}`;
    }
    if (pagination) {
        const offsetVal = pagination.offset > 1 ? (pagination.offset - 1) * 200 : 0;
        filterString += `&offset=${offsetVal}`;
    }

    return filterString;
};
// turns array of ActiveFilter[] objects into query string
export const __assignFilterStringDetailView = (activeFilters: ActiveFilter[], pagination?: Pagination): string => {
    let filterString = '';
    activeFilters.forEach((filter: ActiveFilter) => {
        // Traffic Source
        if (filter.name == 'traffic_source') {
            filterString += `/source/contains:${filter.value}`;
        }
        // URL
        if (filter.name == 'uri') {
            filterString += `/uri/contains:${filter.value}`;
        }
        // Domain
        if (filter.name == 'domain') {
            filterString += `/domain/${filter.value}`;
        }
        // Page Type
        if (filter.name == 'page_type') {
            const pageTypeFilter = _createPageTypeFilterWithConfidenceLevel(filter);
            filterString += `/type/${pageTypeFilter}`;
        }
        // Phrases
        if (filter.name == 'phrases') {
            const phrasesFilter = encodeURIComponent(filter.value);
            filterString += `/keyword/${phrasesFilter}`;
        }
        // Selected
        if (filter.name == 'selected') {
            filter.value === 'clear-filter' ? '' : filterString += `/selected/${filter.value}`;
        }
        return;
    });
    // Sorting and Pagination must come after the filters
    const sortIndex = activeFilters.findIndex((activeFilter: ActiveFilter) => activeFilter.name.includes('sort'));
    if (sortIndex > -1) {
        filterString += `&sort=${activeFilters[sortIndex].value}`;
    }
    if (pagination) {
        const offsetVal = pagination.offset > 1 ? (pagination.offset - 1) * 200 : 0;
        filterString += `&offset=${offsetVal}`;
    }

    return filterString;
};

export const _createPageTypeFilterWithConfidenceLevel = (pageTypeFilter: ActiveFilter,): string => {
    let pageTypeFilterString = '';
    let operand = '';
    if (pageTypeFilter.confidenceLevel == 'high') {
        operand = '>=85';
    }
    if (pageTypeFilter.confidenceLevel == 'medium') {
        operand = '<85';
    }
    if (pageTypeFilter.confidenceLevel == 'low') {
        operand = '<75';
    }
    if (pageTypeFilter.confidenceLevel == 'all') {
        operand = '';
    }

    const pageTypeArr = pageTypeFilter.value.split('|');
    pageTypeArr.map((pageType: string, index: number) => {
        if (index == 0) { // first filter has no | before it
            pageTypeFilterString += `${pageType}${operand}`;
        } else {
            pageTypeFilterString += `|${pageType}${operand}`;
        }
    });

    return pageTypeFilterString;
};

export const __translateFiltersToParamsJobView = (filters: ActiveFilter[], pagination?: Pagination): ParamType => {
    // Turn ActiveFilters[] into displayable URL Jobs page
    const filterParams: ParamType = {};
    filters.forEach((filter: ActiveFilter) => {
        switch (filter.name) {
            case 'created_at': {
                const dates = filter.value.split('..');
                const fromDate = dates[0].split('T')[0];
                const toDate = dates[1].split('T')[0];
                filterParams['created_at'] = `${fromDate}..${toDate}`;
                break;
            }
            case 'reviewed': {
                filter.value === 'clear-filter' ? '' : filterParams['reviewed'] = `${filter.value}`;
                break;
            }
            case 'name': {
                filterParams['name'] = `${filter.value}`;
                break;
            }
            case 'owner': {
                filterParams['owner'] = `${filter.value}`;
                break;
            }
            case 'status': {
                if (filter.value !== '') {
                    filterParams['status'] = `${filter.value}`;
                }
                break;
            }
            case 'requests': {  // URI
                filterParams['requests'] = `${filter.value}`;
                break;
            }
        }
    });
    // Server Side Pagination
    if (pagination) {
        filterParams['page'] = pagination.offset;
    }

    return filterParams;
};


export const __translateFiltersToParamsDetailView = (filters: ActiveFilter[], pagination?: Pagination): ParamType => {
    // Turn ActiveFilters[] into displayable URL (params object) for Job Details page
    const filterParams: ParamType = {};
    filters.forEach((filter: ActiveFilter) => {
        switch (filter.name) {
            case 'uri': {
                filterParams['uri'] = `${filter.value}`;
                break;
            }
            case 'traffic_source': {
                filterParams['traffic_source'] = `${filter.value}`;
                break;
            }
            case 'domain': {
                filterParams['domain'] = `${filter.value}`;
                break;
            }
            case 'page_type': {
                const pageTypeFilter = _createPageTypeFilterWithConfidenceLevel(filter);
                filterParams['page_type'] = `${pageTypeFilter}`;
                break;
            }
            case 'phrases': {
                filterParams['phrases'] = `${filter.value}`;
                break;
            }
            case 'selected': {
                filter.value === 'clear-filter' ? '' : filterParams['selected'] = `${filter.value}`;
                break;
            }
            case 'sort': {
                filterParams['sort'] = `${filter.value}`;
                break;
            }
        }
    });
    // Server Side Pagination
    if (pagination) {
        filterParams['page'] = pagination.offset;
    }

    return filterParams;
};

// Translates FilterParams to ActiveFilter[] (Reverse of __translateFiltersToParams)
export const __translateParamsToFilters = (params: ParamType): ActiveFilter[] => {
    const filters: ActiveFilter[] = [];

    params.forEach((filter: ParamType) => {
        const key = Object.keys(filter)[0];
        let val = '';
        let confidenceLevel = 'all';
        // Different for Page Type with Confidence Level
        if (key == 'page_type') {
            val = filter[key];
            let charToCutAt = '';
            // high confidence
            if (val.includes('>')) {
                confidenceLevel = 'high';
                charToCutAt = '>';
            }
            else if (val.includes('<')) {
                charToCutAt = '<';
                // medium confidence
                if (val.includes('85')) {
                    confidenceLevel = 'medium';
                }
                // low confidence
                else if (val.includes('75')) {
                    confidenceLevel = 'low';
                }
            }
            // if filter == all, return string as-is
            else {
                confidenceLevel = 'all';
                const paramFilter = { name: key, value: val };
                filters.push(paramFilter);
                return;
            }
            const pageTypesArr = val.split('|');
            val = '';
            pageTypesArr.map((x: string, i: number) => {
                if (i == 0) {
                    val += x.split(charToCutAt)[0];
                } else {
                    val += ('|' + x.split(charToCutAt)[0]);
                }
            });
            filters.push({ name: key, value: val, confidenceLevel: confidenceLevel });
        }
        else {
            val = filter[key];
            const paramFilter = { name: key, value: val };
            filters.push(paramFilter);
        }
    });

    return filters;
};

export function __serializeQueryParams(searchParams: URLSearchParams): ParamType[] {
    const params: ParamType[] = [];
    searchParams.forEach((value: string, key: string) => {
        params.push({ [key]: value });
    });
    return params;
}

export const __validateFilterObjects = (filters: ActiveFilter[]): Error | boolean => {
    filters.forEach((filter: ActiveFilter) => {
        const val = filter.value;
        switch (filter.name) {
            case 'name' || 'name.contains': {
                if (val === '') {
                    throw new Error('Invalid value for Name filter');
                }
                break;
            }
            case 'owner': {
                if (val === '') {
                    throw new Error('Invalid value for Owner filter');
                }
                break;
            }
            case 'created_at': {
                if (val === '' || !__isDateRangeValid(val)) {
                    throw new Error('Invalid value for Created At filter');
                }
                break;
            }
            case 'reviewed': {
                if (val === '' || __isStringTrueOrFalse(val) == false) {
                    throw new Error('Invalid value for Reviewed filter');
                }
                break;
            }
            case 'status': {
                if (val != JobStatus.Complete && val != JobStatus.Queueing && val != JobStatus.Queueing && val != JobStatus.Failed && val != JobStatus.Canceled) {
                    throw new Error('Invalid value for Status filter');
                }
                break;
            }
            case 'requests' || 'requests.contains': {
                if (val === '') {
                    throw new Error('Invalid value for Request Contains filter');
                }
                break;
            }
            case 'uri' || 'uri.contains': {
                if (val === '') {
                    throw new Error('Invalid value for Request Contains filter');
                }
                break;
            }
            case 'traffic_source' || 'traffic_source.contains': {
                if (val === '') {
                    throw new Error('Invalid value for Request Contains filter');
                }
                break;
            }
            case 'domain': {
                if (val === '') {
                    throw new Error('Invalid value for Domain filter');
                }
                break;
            }
            case 'page_type': {
                if (val === '') {
                    throw new Error('Invalid value for Type filter');
                }
                break;
            }
            case 'selected': {
                if (val === '' || __isStringTrueOrFalse(val) == false) {
                    throw new Error('Invalid value for Selected filter');
                }
                break;
            }
            case 'phrases': {
                if (val === '') {
                    throw new Error('Invalid value for Phrases filter');
                }
                break;
            }
            case 'page': {
                if (val === '') {
                    throw new Error('Invalid value for Page (offset) filter');
                }
                break;
            }
            case 'limit': {
                if (val === '') {
                    throw new Error('Invalid value for Limit filter');
                }
                break;
            }
            case 'sort': {
                if (val === '') {
                    throw new Error('Invalid value for Sort filter');
                }
                break;
            }
            default: {
                throw new Error('Invalid unknown filter:');
            }
        }
    });
    return true;
};

// Server Side Pagination and Sorting
export const __translatePaginationToParamsDetailView = (pagination: Pagination): ParamType => {
    const paginationParams: ParamType = {};
    paginationParams['page'] = pagination.offset;
    return paginationParams;
};

const localDateToUTC = (dateStr: string): string => {
    // filterString += `/created_at/range:${filter.value.replace(' - ', '..')}`;
    const startDate = dateStr.split('..')[0];
    const endDate = dateStr.split('..')[1];

    // Convert startDate to proper format: MM-DD-YYYY -> YYYY-MM-DD
    const formattedStartDate = moment(startDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate, 'MM-DD-YYYY').format('YYYY-MM-DD');

    // Now, use the formatted date with time (00:00 for start, 23:59 for end)
    const filterStartLocal = moment.tz(`${formattedStartDate} 00:00`, timezone);
    const filterEndLocal = moment.tz(`${formattedEndDate} 23:59`, timezone);

    // Convert the local filter range to UTC
    const filterStartUTC = filterStartLocal.utc();
    const filterEndUTC = filterEndLocal.utc();

    // Convert to API Accepted format: MM-DD-YYYY
    const formattedStartUTC = filterStartUTC.format('MM-DD-YYYY');
    const formattedEndUTC = filterEndUTC.format('MM-DD-YYYY');

    return `${formattedStartUTC}..${formattedEndUTC}`;
};